import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DirectionalHint,
    Dropdown,
    IButtonStyles,
    IDropdownOption,
    IDropdownProps,
    Icon,
    IconButton,
    PrimaryButton,
    Stack,
    Text,
    TextField,
    TooltipHost,
    useTheme,
} from '@fluentui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Activity,
    ProjectDto,
    SetCustomRateForApprovalDto,
    SetMultipleTimeEntryApprovalStateDto,
    SetTimeEntryApprovalActivityDto,
    TimeEntryApprovalDto,
} from '../../../../api/generated/data-contracts';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../api/api';
import { useApprovalStore, isApprovalRow } from '../../../../_grid/grid3';
import shallow from 'zustand/shallow';
import { useTimeUIConfig } from '../../../../hooks/queries/useTimeUIConfig';
import { useConfigValues } from '../../../../hooks/queries/useConfigValues';
import produce from 'immer';

export const SelectionBar = () => {
    const { selectedIds, setSelectedIds, approvals, projects, setApprovals, rows } = useApprovalStore(
        store => ({
            selectedIds: store.selectedRows,
            setSelectedIds: store.setSelectedRows,
            approvals: store.approvals,
            setApprovals: store.setApprovals,
            projects: store.projects,
            rows: store.rows,
        }),
        shallow,
    );
    const [selectedActivity, setSelectedActivity] = useState<IDropdownOption<Activity> | null>(null);

    /** Only the approval rows that are currently visible (i.e. pass the active filters). */
    const approvalRows = useMemo(() => {
        const visibleApprovalIds = new Set(rows.filter(isApprovalRow).map(r => r.id));
        return Object.values(approvals).filter(row => visibleApprovalIds.has(row.id));
    }, [approvals, rows]);

    const selectedApprovalRowStates = useMemo(() => approvalRows.filter(approvalRow => selectedIds.has(approvalRow.id)), [approvalRows, selectedIds]);

    useEffect(() => {
        if (!selectedIds.size) {
            setSelectedActivity(null);
        }
    }, [selectedIds]);

    const checkBoxState = useMemo(() => {
        if (!selectedIds.size) {
            return {
                indeterminate: false,
                checked: false,
            };
        }
        if (selectedIds.size === approvalRows.length) {
            return {
                indeterminate: false,
                checked: true,
            };
        }
        return {
            indeterminate: true,
            checked: false,
        };
    }, [approvalRows.length, selectedIds.size]);

    const theme = useTheme();
    const { activities } = useApprovalStore(store => ({ activities: store.activities }), shallow);

    const options = useMemo(
        (): IDropdownOption<Activity>[] =>
            Object.values(activities).map(activity => {
                return {
                    key: activity.id,
                    text: activity.name,
                    data: activity,
                };
            }),
        [activities],
    );

    const { mutate: mutateActivities } = useMutation((approvals: TimeEntryApprovalDto[]) =>
        ApiCalls.setMultipleActivity({
            approvals: approvals.map((approval): SetTimeEntryApprovalActivityDto => {
                return {
                    // When choosing custom rate then approval.activityId will be null and selectedActivity should be null
                    selectedActivity: activities[approval.activityId] || null,
                    timeEntryId: approval.timeEntry.id,
                    approvalId: approval.id,
                };
            }),
        }),
    );

    const { mutate: mutateApprovalStates } = useMutation((approvals: SetMultipleTimeEntryApprovalStateDto) => ApiCalls.setMultipleApprovalState(approvals));

    const [bulkApprovals, setBulkApprovals] = useState<BulkApprovals | null>(null);

    const bulkApprove = useCallback(async () => {
        const finishedProjects = getFinishedProjectsFromSelection(approvalRows, selectedApprovalRowStates, Object.values(projects));
        if (finishedProjects.length) {
            setBulkApprovals({ finishedProjects, selectedRowStates: selectedApprovalRowStates });
        } else {
            setApprovals(
                produce(approvals => {
                    selectedIds.forEach(id => {
                        approvals[id].approvalState = 'Approved';
                    });
                }),
            );

            mutateApprovalStates({
                approvals: selectedApprovalRowStates.map(approvalRow => ({
                    timeEntryId: approvalRow.timeEntry.id,
                    selectedApprovalState: 'Approved',
                    approvalId: approvalRow.id,
                })),
            });
        }
    }, [approvalRows, mutateApprovalStates, projects, selectedApprovalRowStates, selectedIds, setApprovals]);

    const onChangeActivity = useCallback(
        async (e: any, opt: IDropdownOption<Activity>) => {
            if (opt?.data) {
                setSelectedActivity(opt);
                const activityRate = opt.data.rate;
                const nonApprovedApprovals = selectedApprovalRowStates
                    .filter(row => approvals[row.id].approvalState !== 'Approved')
                    .map(approval => ({
                        ...approval,
                        activityId: opt.data.id,
                    }));
                setApprovals(
                    produce(approvals => {
                        nonApprovedApprovals.forEach(({ id, activityId }) => {
                            approvals[id].activityId = activityId;
                            // If the activity has a rate, update the approval rate; otherwise keep the existing rate
                            if (activityRate != null) {
                                approvals[id].rate = activityRate;
                            }
                        });
                    }),
                );
                mutateActivities(nonApprovedApprovals);
            }
        },
        [approvals, mutateActivities, selectedApprovalRowStates, setApprovals],
    );

    const { config } = useTimeUIConfig();
    const { data: configData } = useConfigValues();
    const enforceRate = configData?.configuration?.approval?.projectManagerApproval?.enforceRateBeforeApproval;

    const [showManualRate, setShowManualRate] = useState(false);

    const [localRateState, setLocalRateState] = useState('');

    const { mutate: mutateCustomRateMultiple } = useMutation((approvals: SetCustomRateForApprovalDto[]) => ApiCalls.setCustomRateMultiple({ approvals }));

    const onBlur = useCallback(() => {
        const newRate = Number(localRateState);
        setShowManualRate(false);
        if (!Number.isNaN(newRate)) {
            setSelectedActivity(null);
            const nonApprovedApprovals = selectedApprovalRowStates
                .filter(row => approvals[row.id].approvalState !== 'Approved')
                .map(approval => ({
                    ...approval,
                    activityId: null,
                }));
            setApprovals(
                produce(approvals => {
                    nonApprovedApprovals.forEach(({ id, activityId }) => {
                        approvals[id].activityId = activityId;
                        approvals[id].rate = newRate;
                    });
                }),
            );

            mutateCustomRateMultiple(
                nonApprovedApprovals.map(approval => ({
                    rate: newRate,
                    timeEntryId: approval.timeEntry.id,
                })),
            );
        }
    }, [approvals, localRateState, mutateCustomRateMultiple, selectedApprovalRowStates, setApprovals]);

    const onReset = useCallback(() => {
        const newRate = Number(localRateState);
        setShowManualRate(false);
        if (!Number.isNaN(newRate)) {
            setSelectedActivity(null);
            const nonApprovedApprovals = selectedApprovalRowStates
                .filter(row => approvals[row.id].approvalState !== 'Approved')
                .map(approval => ({
                    ...approval,
                    activityId: null,
                }));
            setApprovals(
                produce(approvals => {
                    nonApprovedApprovals.forEach(({ id, activityId, originalRate }) => {
                        approvals[id].activityId = activityId;
                        approvals[id].rate = originalRate;
                    });
                }),
            );

            mutateCustomRateMultiple(
                nonApprovedApprovals.map(approval => ({
                    rate: null,
                    timeEntryId: approval.timeEntry.id,
                })),
            );
        }
    }, [approvals, localRateState, mutateCustomRateMultiple, selectedApprovalRowStates, setApprovals]);

    const selectedMissingRateCount = useMemo(() => {
        if (!enforceRate) return 0;
        return selectedApprovalRowStates.filter(row => {
            const approval = approvals[row.id];
            const hasActivity = approval.activityId != null;
            if (hasActivity) return false; // Activity selected — allow approval even without a rate
            const hasRate = approval.rate != null || approval.originalRate != null;
            return !hasRate;
        }).length;
    }, [enforceRate, selectedApprovalRowStates, approvals, activities]);

    const approveDisabled = enforceRate && selectedMissingRateCount > 0;

    const approveButtonStyles: IButtonStyles = useMemo(
        () => ({
            root: { backgroundColor: theme.semanticColors.successIcon, borderColor: theme.semanticColors.successIcon, color: '#ffffff' },
            rootHovered: { backgroundColor: '#0a5d0a', borderColor: '#0a5d0a', color: '#ffffff' },
            rootPressed: { backgroundColor: '#084508', borderColor: '#084508', color: '#ffffff' },
        }),
        [theme.semanticColors.successIcon],
    );

    return (
        <>
            <Stack
                horizontal
                horizontalAlign="space-between"
                styles={{ root: { padding: '0 8px', border: `1px solid ${theme.semanticColors.disabledBackground}`, height: 60 } }}
            >
                <Stack horizontal verticalAlign="center">
                    <Checkbox
                        {...checkBoxState}
                        onChange={(e, val) => {
                            if (val) {
                                setSelectedIds(new Set(approvalRows.map(approval => approval.id)));
                            } else {
                                setSelectedIds(new Set());
                            }
                        }}
                    />
                    <Text>Select all</Text>
                    {selectedIds.size !== 0 && (
                        <>
                            <div
                                style={{
                                    display: 'inline-block',
                                    height: '1rem',
                                    width: 1,
                                    backgroundColor: theme.semanticColors.bodyText,
                                    marginLeft: 10,
                                    marginRight: 10,
                                }}
                            />
                            <Text>{selectedIds.size} Selected</Text>
                        </>
                    )}
                </Stack>
                <Stack horizontal verticalAlign="center" styles={{ root: { height: '100%', gap: 20 } }}>
                    {selectedIds.size !== 0 && (
                        <>
                            {Boolean(config.approval.projectManagerApproval.useRates) && (
                                <Stack horizontal verticalAlign="center">
                                    {showManualRate ? (
                                        <TextField
                                            autoFocus
                                            styles={{ root: { width: 200 } }}
                                            type="number"
                                            onChange={(e, newValue) => {
                                                setLocalRateState(newValue);
                                            }}
                                            value={String(localRateState)}
                                            onBlur={onBlur}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    (e.target as HTMLDivElement).blur();
                                                }
                                            }}
                                            placeholder="Enter custom rate"
                                        />
                                    ) : (
                                        <Dropdown
                                            styles={dropdownStyles}
                                            options={options}
                                            selectedKey={(selectedActivity?.key as string) || null}
                                            onChange={onChangeActivity}
                                            placeholder="Bulk edit activity"
                                        />
                                    )}
                                    <TooltipHost
                                        content={`${showManualRate ? 'Save' : 'Set'} billable adjustment`}
                                        directionalHint={DirectionalHint.topLeftEdge}
                                        closeDelay={250}
                                    >
                                        {showManualRate ? (
                                            <IconButton
                                                //
                                                key="save"
                                                iconProps={{ iconName: 'Save' }}
                                                data-testid="save"
                                                style={{ fontSize: 12, cursor: 'pointer' }}
                                                onMouseUp={onBlur}
                                            />
                                        ) : (
                                            <IconButton
                                                key="edit"
                                                iconProps={{ iconName: 'Edit' }}
                                                data-testid="edit"
                                                style={{ fontSize: 12, cursor: 'pointer' }}
                                                onClick={() => setShowManualRate(true)}
                                            />
                                        )}
                                    </TooltipHost>
                                    <TooltipHost content="Revert to default" directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                                        <IconButton
                                            iconProps={{ iconName: 'RevToggleKey' }}
                                            data-testid="delete"
                                            style={{ fontSize: 12, cursor: 'pointer' }}
                                            onClick={onReset}
                                        />
                                    </TooltipHost>
                                </Stack>
                            )}
                            <TooltipHost
                                content={
                                    approveDisabled
                                        ? `${selectedMissingRateCount} selected ${selectedMissingRateCount === 1 ? 'entry is' : 'entries are'} missing a rate`
                                        : undefined
                                }
                            >
                                <PrimaryButton styles={approveButtonStyles} onClick={bulkApprove} disabled={approveDisabled}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                        <Text styles={{ root: { color: 'inherit' } }}>Approve</Text>
                                        <Icon iconName="CheckMark" styles={{ root: { color: 'inherit' } }} />
                                    </Stack>
                                </PrimaryButton>
                            </TooltipHost>
                        </>
                    )}
                </Stack>
            </Stack>
            {bulkApprovals && (
                <BulkApprovalDialog
                    bulkApprovals={bulkApprovals}
                    onApprove={() => {
                        const { selectedRowStates } = bulkApprovals;
                        setApprovals(
                            produce(approvals => {
                                selectedRowStates.forEach(row => {
                                    approvals[row.id].approvalState = 'Approved';
                                });
                            }),
                        );
                        mutateApprovalStates({
                            approvals: selectedRowStates.map(appr => ({
                                selectedApprovalState: 'Approved',
                                timeEntryId: appr.timeEntry.id,
                                approvalId: appr.id,
                            })),
                        });
                        setBulkApprovals(null);
                    }}
                    onDismiss={() => setBulkApprovals(null)}
                />
            )}
        </>
    );
};

const getFinishedProjectsFromSelection = (allApprovals: TimeEntryApprovalDto[], selectedApprovals: TimeEntryApprovalDto[], projects: ProjectDto[]) => {
    return projects.filter(project => {
        const missingApprovalsForProject = allApprovals.filter(approval => approval.projectId === project.id && approval.approvalState !== 'Approved');
        return (
            missingApprovalsForProject.length &&
            missingApprovalsForProject.every(missingApproval => selectedApprovals.find(selectedApproval => selectedApproval.id === missingApproval.id))
        );
    });
};

type BulkApprovals = { selectedRowStates: TimeEntryApprovalDto[]; finishedProjects: ProjectDto[] };

const BulkApprovalDialog = ({
    bulkApprovals: { finishedProjects },
    onApprove,
    onDismiss,
}: {
    bulkApprovals: BulkApprovals;
    onDismiss: () => void;
    onApprove: () => void;
}) => {
    const theme = useTheme();
    const approveButtonStyles: IButtonStyles = useMemo(
        () => ({
            root: { backgroundColor: theme.semanticColors.successIcon, borderColor: theme.semanticColors.successIcon, color: '#ffffff' },
            rootHovered: { backgroundColor: '#0a5d0a', borderColor: '#0a5d0a', color: '#ffffff' },
            rootPressed: { backgroundColor: '#084508', borderColor: '#084508', color: '#ffffff' },
        }),
        [theme.semanticColors.successIcon],
    );
    return (
        <Dialog onDismiss={onDismiss} hidden={false} dialogContentProps={{ title: 'Approve and close time registration?' }} minWidth={500}>
            <Stack tokens={{ childrenGap: 10 }}>
                <Text>
                    After approving all hours, time registration will be closed for the given time period and restricted from further changes on the following
                    projects:
                </Text>
                <ul>
                    {finishedProjects.map(project => {
                        return (
                            <li key={project.id}>
                                <Text>{project.name}</Text>
                            </li>
                        );
                    })}
                </ul>
            </Stack>

            <DialogFooter>
                <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
                <PrimaryButton styles={approveButtonStyles} onClick={onApprove}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <Text styles={{ root: { color: '#ffffff' } }}>Approve</Text>
                        <Icon iconName="CheckMark" styles={{ root: { color: '#ffffff' } }} />
                    </Stack>
                </PrimaryButton>
            </DialogFooter>
        </Dialog>
    );
};

const dropdownStyles: IDropdownProps['styles'] = { root: { width: 200 }, callout: { minWidth: 'min-content' } };
